import React, { FC, useMemo, useState } from 'react';

import { Controller } from 'react-hook-form';

import AdminEntryViewPage from 'components/admin/AdminEntryViewPage';
import AdminFilter from 'components/admin/AdminFilter/view';
import ObjectEntriesView from 'components/admin/ObjectEntriesView';
import ReactSelect from 'components/common/ui-kit/Select/view';
import TableList from 'components/moderator/TableList';
import usePagination from 'hooks/common/usePagination';
import { EntranceTestStatus } from 'models/applications/entrance-tests';
import { SelectOption } from 'models/common';
import {
  ImportTaskResponse,
  ImportTaskStatus,
} from 'models/staff/import-tasks';
import { importTasksApi } from 'redux/api/staff/admin/import-tasks';
import { getMoscowDateTimeFormattedString } from 'utils/date';
import { createSelectProps } from 'utils/form-helpers';

import {
  importTaskStatusesToStrings,
  importTaskTypesToStrings,
} from '../ImportTasksPage';

export const getPartOfUrl = (url: string) => {
  if (!url) return '-';
  const parts = url.split('?');
  if (parts.length < 2) return url;
  return `${parts[0]}...${parts[1].slice(-5)}`;
};

export const entranceTestStatusesToStrings: Record<EntranceTestStatus, string> =
  {
    NOT_STARTED: 'Не началось',
    PASSED: 'Пройдено',
    FAILED: 'Провалено',
  };

export const entranceTestStatusSelectOptions = Object.entries(
  entranceTestStatusesToStrings
).map(([key, value]) => ({
  value: key,
  label: value,
}));

export const importTaskStatusOptions = Object.entries(
  importTaskStatusesToStrings
).map(([key, value]) => ({
  value: key,
  label: value,
}));

const DetailedImportTaskView: FC<{
  detailedImportTask: ImportTaskResponse;
}> = ({ detailedImportTask }) => {
  const detailsColumns = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Статус',
      accessor: 'status',
      render: ({ status }) => importTaskStatusesToStrings[status],
    },
    {
      Header: 'Данные',
      accessor: 'payload',
    },
  ];

  const tableData = detailedImportTask.details;

  const initialTableFilters = {
    status: null,
  };
  const [tableFilters, setTableFilters] = useState<
    Partial<{
      status: ImportTaskStatus | null;
    }>
  >(initialTableFilters);

  const filteredTableData = useMemo(() => {
    if (!tableFilters || !tableFilters.status) return tableData;
    return tableData.filter(
      importTask => importTask.status === tableFilters.status
    );
  }, [tableFilters]);

  const { page, setPage, size, setSize } = usePagination();

  const total = filteredTableData.length;

  const tableDataOffset = (page - 1) * size;
  const tablePageData = filteredTableData.slice(
    tableDataOffset,
    tableDataOffset + size
  );

  return (
    <div className="registry-entry-view">
      <ObjectEntriesView<ImportTaskResponse>
        object={detailedImportTask}
        keysToTitles={{
          id: 'ID',
          type: 'Тип',
          status: 'Статус',
          createdAt: 'Дата создания',
          updatedAt: 'Дата изменения',
        }}
        keysToRenderers={{
          createdAt: dateTimeOfTest => {
            return getMoscowDateTimeFormattedString(dateTimeOfTest);
          },
          updatedAt: dateTimeOfTest => {
            return getMoscowDateTimeFormattedString(dateTimeOfTest);
          },
          type: type => {
            return importTaskTypesToStrings[type];
          },
          status: status => {
            return importTaskStatusesToStrings[status];
          },
        }}
      />

      <AdminFilter
        onSubmit={data => {
          setPage(1);
          setTableFilters(data);
        }}
        onReset={() => {
          setPage(1);
          setTableFilters(initialTableFilters);
        }}
        filter={tableFilters}
        defaultValues={initialTableFilters}
      >
        {({ control, formState: { errors } }) => {
          const selectProps = createSelectProps(errors);

          return (
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <ReactSelect<SelectOption>
                  options={importTaskStatusOptions}
                  placeholder="Статус"
                  className="filter-form__field"
                  {...selectProps(field, {
                    options: importTaskStatusOptions,
                  })}
                />
              )}
            />
          );
        }}
      </AdminFilter>

      <TableList
        columns={detailsColumns}
        mappedData={tablePageData}
        showCsvDownloadButton={false}
        onRowClick={null}
        pagination={{
          currentPage: page,
          perPage: size,
          lastPage: Math.ceil(total / size),
          onPageChange: newPage => setPage(newPage),
          onPerPageChange: perPage => setSize(perPage),
        }}
      />
    </div>
  );
};

const ImportTaskPage = () => {
  return (
    <AdminEntryViewPage
      useGetEntryQuery={importTasksApi.useGetDetailedInfoQuery}
      getBreadcrumbsLabel={() => 'Загружаемый документ'}
    >
      {detailedImportTask => (
        <DetailedImportTaskView detailedImportTask={detailedImportTask} />
      )}
    </AdminEntryViewPage>
  );
};

export default ImportTaskPage;
